import React from 'react';
import { Box, Button, ButtonBase, Drawer, IconButton, Typography } from '@mui/material';
import { rightDrawerBelowAppBarSx } from '../../theme/layoutConstants';
import { Company, Prov } from './lib/types';
import { tint } from './lib/tone';
import { ActionRow } from './lib/selectors';
import { fmtIct } from './lib/ict';
import { ImpactTiles } from './ImpactTiles';
import { GaChip, NoReviewChip, PriorityChip, ProvLine, RecommendedLabel, VerdictChip } from './chips';
import { ACCENT, FONT, WARN_TEXT, OVERLINE, RADIUS, TONE, accentText } from './lib/brand';

const NOT_STATED = 'Not stated in source';

/** The item's sources, each once (the finding and the recommendation often cite the same line). */
function uniqueProvs(list: (Prov | null | undefined)[]): Prov[] {
  const seen = new Set<string>();
  return list.filter((p): p is Prov => {
    if (!p || !p.path) return false;
    const k = `${p.path}:${p.line ?? ''}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/** Copy a source path (path:line) for opening in an editor; says so for 2 s. */
function CopyPath({ path }: { path: string }) {
  const [done, setDone] = React.useState<'ok' | 'fail' | null>(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(path); setDone('ok'); } catch { setDone('fail'); }
    window.setTimeout(() => setDone(null), 2000);
  };
  return (
    <Button size="small" onClick={copy} aria-label={`Copy source path ${path}`} sx={{ minWidth: 0, flexShrink: 0, py: 0, fontSize: 11.5, textTransform: 'none' }}>
      {done === 'ok' ? 'Copied' : done === 'fail' ? 'Copy failed' : 'Copy path'}
    </Button>
  );
}

const Section = ({ title, children, id }: { title: string; children: React.ReactNode; id?: string }) => (
  <Box component="section" id={id} sx={{ mb: 2.5, scrollMarginTop: 56 }}>
    <Typography variant="overline" sx={{ ...OVERLINE, display: 'block', fontSize: 10.5, mb: 0.6 }}>{title}</Typography>
    {children}
  </Box>
);

/**
 * One recommendation in full (DASHBOARD.md §1.5): decision, recommended action,
 * the finding with its key facts, sources, the C-suite verdict and the question
 * thread. Missing values read "Not stated in source"; nothing is invented. The
 * decision and question sections are slots, filled in Phase 2.
 */
export function RecommendationDrawer({
  row, company, onClose, onOpenBot, onShowInActionCenter, onCopyLink, onGa, renderDecision, renderQuestion, onWhy,
}: {
  row: ActionRow | null; company: Company; onClose: () => void;
  onOpenBot: (slug: string) => void; onShowInActionCenter: (row: ActionRow) => void; onCopyLink: () => void; onGa: (id: string) => void;
  renderDecision?: (row: ActionRow) => React.ReactNode; renderQuestion?: (row: ActionRow) => React.ReactNode; onWhy?: (row: ActionRow) => void;
}) {
  const accent = ACCENT[company].acc;
  const a = row && row.action;
  const kv = (k: string, v?: string | null | boolean) => (
    <Box key={k} sx={{ display: 'flex', gap: 1.5, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', fontSize: 13 }}>
      <Box sx={{ width: 150, flexShrink: 0, color: 'text.secondary' }}>{k}</Box>
      <Box sx={{ minWidth: 0, wordBreak: 'break-word', color: v == null || v === '' ? 'text.secondary' : 'text.primary' }}>
        {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v == null || v === '' ? NOT_STATED : v}
      </Box>
    </Box>
  );
  return (
    <Drawer
      anchor="right"
      open={Boolean(row)}
      onClose={onClose}
      PaperProps={{
        role: 'dialog', 'aria-modal': true, 'aria-label': a ? `Recommendation: ${a.for || a.text || ''}` : 'Recommendation', 'data-testid': 'recommendation-drawer',
        sx: { ...rightDrawerBelowAppBarSx, width: { xs: '100%', sm: 560 } },
      }}
    >
      {row && a && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box sx={{ p: 2, bgcolor: tint(ACCENT[company].soft), borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>
                <ButtonBase onClick={() => onOpenBot(row.bot.slug)} sx={{ color: accentText(accent), textDecoration: 'underline', fontSize: 'inherit' }}>{row.bot.name}</ButtonBase> › Recommendation
              </Typography>
              <IconButton aria-label="Close" onClick={onClose} size="small" autoFocus>✕</IconButton>
            </Box>
            <Typography component="h2" sx={{ fontFamily: FONT.heading, fontSize: 17, fontWeight: 700, my: 0.8 }}>{a.for || a.text || 'Untitled item'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
              <PriorityChip priority={a.priority} />
              {a.priority_code && <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 10.5, fontWeight: 700, px: 0.8, borderRadius: RADIUS.sm, bgcolor: 'action.selected' }}>{a.priority_code}</Box>}
              {a.review ? <VerdictChip exec={a.review.exec} verdict={a.review.verdict} /> : <NoReviewChip level="item" />}
              <Box component="span" sx={{ fontFamily: FONT.mono, fontSize: 10.5, color: 'text.secondary' }}>id {a.id}</Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2 }}>
              <Button size="small" variant="outlined" onClick={() => onOpenBot(row.bot.slug)}>Open bot</Button>
              <Button size="small" variant="outlined" onClick={() => onShowInActionCenter(row)}>Open task in Action Center</Button>
              {onWhy && <Button size="small" variant="outlined" onClick={() => onWhy(row)}>Why?</Button>}
              <Button size="small" variant="outlined" onClick={onCopyLink}>Copy deep link</Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Section title="Recommended action">
              <RecommendedLabel action={a} accent={accent} />
              <Typography sx={{ fontSize: 14, mt: 0.8 }}>{a.recommended_action || NOT_STATED}</Typography>
              {a.recommended_prov && <Box sx={{ mt: 0.4 }}><ProvLine prov={a.recommended_prov} fmt={fmtIct} /></Box>}
              {a.bot_original && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1, mt: 1 }}>Bot proposed: {a.bot_original}</Typography>
              )}
            </Section>

            <Section title="What changes and the impact">
              <ImpactTiles action={a} />
              <Box sx={{ mt: 1 }}>
                {kv('Expected impact', a.expected_impact)}
                {kv('Needs Grant approval', a.needs_grant_approval)}
              </Box>
            </Section>

            <Section title="Finding">
              {kv('Finding', a.finding)}
              {kv('Source date', a.source_date)}
              {kv('Owner bot', row.bot.name)}
            </Section>

            <Section title="Your decision" id="bo-r-decision">
              {renderDecision ? renderDecision(row) : (
                <Typography variant="body2" color="text.secondary" data-testid="decision-placeholder">
                  {a.decidable ? 'Decisions (Yes / No / Instruct instead) are switched on in the next release.' : 'No decision: this is a blocker, not a proposal.'}
                </Typography>
              )}
            </Section>

            <Section title="C-suite verdict" id="bo-r-verdict">
              {!a.review ? <NoReviewChip level="item" /> : (
                <Box sx={{ border: '1px solid', borderColor: a.review.verdict === 'alternative' ? TONE.warn.border : TONE.good.border, bgcolor: tint(a.review.verdict === 'alternative' ? TONE.warn.bg : TONE.good.bg), borderRadius: RADIUS.lg, p: 1.2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.6 }}>
                    <VerdictChip exec={a.review.exec} verdict={a.review.verdict} />
                    {a.review.awaiting_grant && <Box component="span" sx={{ fontSize: 10.5, fontWeight: 700, px: 0.8, borderRadius: RADIUS.sm, bgcolor: TONE.warn.bg, color: TONE.warn.fg }}>AWAITING GRANT</Box>}
                  </Box>
                  {a.review.bot_rec && a.review.bot_rec.text && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.6 }}>Bot recommendation: {a.review.bot_rec.text}</Typography>}
                  <Typography sx={{ fontSize: 13 }}>{a.review.text || NOT_STATED}</Typography>
                  {a.review.grant_note && a.review.grant_note.text && <Typography sx={{ fontSize: 12.5, color: WARN_TEXT, mt: 0.6 }}>⚑ {a.review.grant_note.text}</Typography>}
                  {(a.review.ga || []).length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.8 }}>{(a.review.ga || []).map((g) => <GaChip key={g.id} ga={g} onClick={onGa} />)}</Box>
                  )}
                  <Box sx={{ mt: 0.6 }}><ProvLine prov={a.review.prov} fmt={fmtIct} /></Box>
                </Box>
              )}
            </Section>

            <Section title="Sources">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {uniqueProvs([a.prov, a.recommended_prov, a.review && a.review.prov]).map((p) => (
                  <Box key={`${p.path}:${p.line ?? ''}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}><ProvLine prov={p} fmt={fmtIct} /></Box>
                    <CopyPath path={p.line ? `${p.path}:${p.line}` : p.path} />
                  </Box>
                ))}
              </Box>
            </Section>

            <Section title="Ask a question" id="bo-r-question">
              {renderQuestion ? renderQuestion(row) : (
                <Typography variant="body2" color="text.secondary" data-testid="question-placeholder">Questions to {row.bot.short} are switched on in the next release.</Typography>
              )}
            </Section>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
